import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Button } from '../ui/button';
import { Activity, Loader2, ChevronDown, ChevronUp } from 'lucide-react';
import ActivityCard from '../notifications/ActivityCard';
import FomChangesModal from '../FomChangesModal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PER_PAGE = 5;

const TIPO_OPTIONS = [
  { value: '', label: '📋 Todos (Labor Bills + Supervisor)' },
  { value: 'labor_bill_created', label: '📋 Labor Bill Criado' },
  { value: 'labor_bill_edited', label: '✏️ Labor Bill Editado' },
  { value: 'labor_bill_sent', label: '📧 Labor Bill Enviado' },
  { value: 'supervisor', label: '👷 Supervisor — Tudo' },
  { value: 'supervisor_changed', label: '👷✏️ Supervisor — Alterados' },
  { value: 'supervisor_unchanged', label: '👷✅ Supervisor — Sem alterações' },
];

// Card do dashboard (SOMENTE ADMIN): atividades de Labor Bills + Supervisor
const DashboardActivityFeed = ({ language = 'pt-BR' }) => {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [teams, setTeams] = useState([]);
  const [filters, setFilters] = useState({ pm: '', tipo: '', dateFrom: '', dateTo: '' });
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem('homeActivityFeedCollapsed') !== '0');
  const [fomReview, setFomReview] = useState(null);

  const toggleCollapsed = () => {
    const next = !collapsed;
    setCollapsed(next);
    localStorage.setItem('homeActivityFeedCollapsed', next ? '1' : '0');
  };

  useEffect(() => {
    axios.get(`${API}/admin/team-reset/options`)
      .then(({ data }) => setTeams((data.teams || []).filter(t => t.username !== 'DEMO')))
      .catch(() => setTeams([]));
  }, []);

  const load = useCallback(async (p = 1, f = filters) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: String(p), limit: String(PER_PAGE) });
      params.append('tipo', f.tipo || 'labor_and_supervisor');
      if (f.pm) params.append('pm', f.pm);
      if (f.dateFrom) params.append('date_from', f.dateFrom);
      if (f.dateTo) params.append('date_to', f.dateTo);
      const { data } = await axios.get(`${API}/admin/notifications?${params.toString()}`);
      setItems(data.items || []);
      setTotal(data.total || 0);
      setPage(p);
    } catch (e) {
      setItems([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => { load(1); }, []);

  const setFilter = (key, value) => {
    const f = { ...filters, [key]: value };
    setFilters(f);
    load(1, f);
  };

  const totalPages = Math.max(1, Math.ceil(total / PER_PAGE));

  return (
    <div className="col-span-full">
      <div className="rounded-xl border border-sky-200 border-l-4 border-l-sky-500 bg-white shadow-sm p-4" data-testid="home-activity-feed">
        <div
          className="flex items-center justify-between flex-wrap gap-2 cursor-pointer select-none"
          onClick={toggleCollapsed}
          data-testid="home-activity-header"
        >
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-sky-500 to-blue-600 flex items-center justify-center shadow-md">
              <Activity className="h-5 w-5 text-white stroke-[1.5]" />
            </div>
            <div>
              <span className="font-semibold text-slate-800 block leading-tight">
                {language === 'pt-BR' ? 'Atividades — Labor Bills & Supervisor' : 'Activity — Labor Bills & Supervisor'}
              </span>
              <span className="text-xs text-slate-500" data-testid="home-activity-total">
                {total} {language === 'pt-BR' ? 'registro(s)' : 'record(s)'}
              </span>
            </div>
          </div>
          <button
            type="button"
            className="p-1.5 rounded-md hover:bg-slate-100 text-slate-500"
            data-testid="home-activity-collapse-btn"
            onClick={(e) => { e.stopPropagation(); toggleCollapsed(); }}
            aria-label={collapsed ? 'Expandir' : 'Minimizar'}
          >
            {collapsed ? <ChevronDown className="h-5 w-5" /> : <ChevronUp className="h-5 w-5" />}
          </button>
        </div>

        {!collapsed && (
        <div className="mt-3">
        {/* Filtros */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-3">
          <select
            data-testid="home-activity-filter-tipo"
            value={filters.tipo}
            onChange={(e) => setFilter('tipo', e.target.value)}
            className="border rounded-md px-2 py-1.5 text-xs bg-white col-span-2 sm:col-span-1"
          >
            {TIPO_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
          <select
            data-testid="home-activity-filter-pm"
            value={filters.pm}
            onChange={(e) => setFilter('pm', e.target.value)}
            className="border rounded-md px-2 py-1.5 text-xs bg-white"
          >
            <option value="">{language === 'pt-BR' ? '🏷️ Todas equipes' : '🏷️ All teams'}</option>
            {teams.map(t => <option key={t.username} value={t.username}>{t.username}</option>)}
          </select>
          <input
            data-testid="home-activity-filter-from"
            type="date"
            value={filters.dateFrom}
            onChange={(e) => setFilter('dateFrom', e.target.value)}
            className="border rounded-md px-2 py-1.5 text-xs bg-white"
          />
          <input
            data-testid="home-activity-filter-to"
            type="date"
            value={filters.dateTo}
            onChange={(e) => setFilter('dateTo', e.target.value)}
            className="border rounded-md px-2 py-1.5 text-xs bg-white"
          />
        </div>

        {/* Lista */}
        {loading ? (
          <div className="flex items-center justify-center py-8 text-slate-500 text-sm">
            <Loader2 className="w-4 h-4 animate-spin mr-2" /> {language === 'pt-BR' ? 'Carregando...' : 'Loading...'}
          </div>
        ) : items.length === 0 ? (
          <div className="text-center text-slate-400 py-8 text-sm" data-testid="home-activity-empty">
            {language === 'pt-BR' ? 'Nenhuma atividade encontrada com esses filtros' : 'No activity found with these filters'}
          </div>
        ) : (
          <div data-testid="home-activity-list">
            {items.map((activity, idx) => (
              <ActivityCard
                key={activity.id || idx}
                activity={activity}
                language={language}
                onOpenChanges={(ex) => setFomReview(ex)}
              />
            ))}
          </div>
        )}

        {/* Paginação */}
        {totalPages > 1 && (
          <div className="flex justify-between items-center pt-2 border-t">
            <span className="text-xs text-slate-500">
              {language === 'pt-BR' ? `Página ${page} de ${totalPages}` : `Page ${page} of ${totalPages}`}
            </span>
            <div className="flex gap-2">
              <Button data-testid="home-activity-prev" variant="outline" size="sm" className="text-xs" disabled={page <= 1} onClick={() => load(page - 1)}>
                {language === 'pt-BR' ? 'Anterior' : 'Prev'}
              </Button>
              <Button data-testid="home-activity-next" variant="outline" size="sm" className="text-xs" disabled={page >= totalPages} onClick={() => load(page + 1)}>
                {language === 'pt-BR' ? 'Próxima' : 'Next'}
              </Button>
            </div>
          </div>
        )}
        </div>
        )}
      </div>
      <FomChangesModal open={!!fomReview} onClose={() => setFomReview(null)} data={fomReview} />
    </div>
  );
};

export default DashboardActivityFeed;
